// Step types for type safety
export type AmbassadorSignupStep =
  | 'welcome'
  | 'start'
  | 'share'
  | 'language'
  | 'email'
  | 'confirm'
  | 'alreadyExist'
  | 'phone'
  | 'verification-phone'
  | 'name'
  | 'profile'
  | 'address'
  | 'actionsInterestedIn'
  | 'touchPoints'
  | 'resume';

// Touchpoint configuration
export interface TouchPointConfig {
  prefix: string | null;
  displayPrefix: string | null; // Shorter prefix for display (without https://)
  placeholder: string;
  validationType: 'phoneNumber' | 'pseudo' | 'email' | 'url';
}

export const TOUCHPOINT_CONFIG: Record<string, TouchPointConfig> = {
  instagram: {
    prefix: 'https://www.instagram.com/',
    displayPrefix: 'instagram.com/',
    placeholder: 'username',
    validationType: 'pseudo',
  },
  facebook: {
    prefix: 'https://www.facebook.com/',
    displayPrefix: 'facebook.com/',
    placeholder: 'username',
    validationType: 'pseudo',
  },
  'X (Twitter)': {
    prefix: 'https://www.twitter.com/',
    displayPrefix: 'twitter.com/',
    placeholder: 'username',
    validationType: 'pseudo',
  },
  telegram: {
    prefix: 'https://t.me/',
    displayPrefix: 't.me/',
    placeholder: 'username',
    validationType: 'pseudo',
  },
  tiktok: {
    prefix: 'https://www.tiktok.com/@',
    displayPrefix: 'tiktok.com/@',
    placeholder: 'username',
    validationType: 'pseudo',
  },
  youtube: {
    prefix: 'https://www.youtube.com/channel/',
    displayPrefix: 'youtube.com/channel/',
    placeholder: 'username',
    validationType: 'pseudo',
  },
  weChat: {
    prefix: 'https://weixin.qq.com/',
    displayPrefix: 'weixin.qq.com/',
    placeholder: 'username',
    validationType: 'pseudo',
  },
  linkedin: {
    prefix: 'https://linkedin.com/in/',
    displayPrefix: 'linkedin.com/in/',
    placeholder: 'username',
    validationType: 'pseudo',
  },
  discord: {
    prefix: null,
    displayPrefix: null,
    placeholder: 'username',
    validationType: 'pseudo',
  },
  reddit: {
    prefix: 'https://www.reddit.com/user/',
    displayPrefix: 'reddit.com/user/',
    placeholder: 'username',
    validationType: 'pseudo',
  },
  calendar: {
    prefix: null,
    displayPrefix: null,
    placeholder: 'url',
    validationType: 'url',
  },
  whatsapp: {
    prefix: null,
    displayPrefix: null,
    placeholder: 'phone',
    validationType: 'phoneNumber',
  },
  line: {
    prefix: 'https://line.me/',
    displayPrefix: 'line.me/',
    placeholder: 'username',
    validationType: 'pseudo',
  },
  phone: {
    prefix: null,
    displayPrefix: null,
    placeholder: 'phone',
    validationType: 'phoneNumber',
  },
  messenger: {
    prefix: 'https://m.me/',
    displayPrefix: 'm.me/',
    placeholder: 'username',
    validationType: 'pseudo',
  },
  snapchat: {
    prefix: 'https://www.snapchat.com/add/',
    displayPrefix: 'snapchat.com/add/',
    placeholder: 'username',
    validationType: 'pseudo',
  },
  littleredbook: {
    prefix: null,
    displayPrefix: null,
    placeholder: 'url',
    validationType: 'url',
  },
  mail: {
    prefix: null,
    displayPrefix: null,
    placeholder: 'email',
    validationType: 'email',
  },
};

// Step flow configuration - makes it easy to add/remove steps
export interface StepConfig {
  id: AmbassadorSignupStep;
  component: string;
  title?: string;
  canGoBack: boolean;
  requiresValidation: boolean;
  hasHeader: boolean;
  hasStartHeader: boolean;
  hasProgressDots: boolean;
  hasContinueButton: boolean;
}

export const STEP_FLOW: StepConfig[] = [
  {
    id: 'welcome',
    component: 'Welcome',
    canGoBack: false,
    requiresValidation: false,
    hasHeader: false,
    hasStartHeader: true,
    hasProgressDots: true,
    hasContinueButton: true,
  },
  {
    id: 'start',
    component: 'Start',
    canGoBack: true,
    requiresValidation: false,
    hasHeader: false,
    hasStartHeader: true,
    hasProgressDots: true,
    hasContinueButton: true,
  },
  {
    id: 'share',
    component: 'Share',
    canGoBack: true,
    requiresValidation: false,
    hasHeader: false,
    hasStartHeader: true,
    hasProgressDots: true,
    hasContinueButton: true,
  },
  {
    id: 'language',
    component: 'AppLanguages',
    canGoBack: true,
    requiresValidation: false,
    hasHeader: true,
    hasStartHeader: false,
    hasProgressDots: false,
    hasContinueButton: true,
  },
  {
    id: 'email',
    component: 'Email',
    canGoBack: true,
    requiresValidation: true,
    hasHeader: true,
    hasStartHeader: false,
    hasProgressDots: false,
    hasContinueButton: true,
  },
  {
    id: 'confirm',
    component: 'Confirm',
    canGoBack: false,
    requiresValidation: false,
    hasHeader: false,
    hasStartHeader: false,
    hasProgressDots: false,
    hasContinueButton: false,
  },
  {
    id: 'alreadyExist',
    component: 'AlreadyExist',
    canGoBack: false,
    requiresValidation: false,
    hasHeader: false,
    hasStartHeader: false,
    hasProgressDots: false,
    hasContinueButton: false,
  },
  {
    id: 'phone',
    component: 'Phone',
    canGoBack: true,
    requiresValidation: true,
    hasHeader: true,
    hasStartHeader: false,
    hasProgressDots: false,
    hasContinueButton: true,
  },
  {
    id: 'verification-phone',
    component: 'VerificationPhone',
    canGoBack: true,
    requiresValidation: true,
    hasHeader: true,
    hasStartHeader: false,
    hasProgressDots: false,
    hasContinueButton: true,
  },
  {
    id: 'name',
    component: 'Name',
    canGoBack: false,
    requiresValidation: true,
    hasHeader: true,
    hasStartHeader: false,
    hasProgressDots: false,
    hasContinueButton: true,
  },
  {
    id: 'profile',
    component: 'Profile',
    canGoBack: true,
    requiresValidation: true,
    hasHeader: true,
    hasStartHeader: false,
    hasProgressDots: false,
    hasContinueButton: true,
  },
  {
    id: 'address',
    component: 'PostalAddress',
    canGoBack: true,
    requiresValidation: false,
    hasHeader: true,
    hasStartHeader: false,
    hasProgressDots: false,
    hasContinueButton: true,
  },
  {
    id: 'actionsInterestedIn',
    component: 'Actions',
    canGoBack: true,
    requiresValidation: true,
    hasHeader: true,
    hasStartHeader: false,
    hasProgressDots: false,
    hasContinueButton: true,
  },
  {
    id: 'touchPoints',
    component: 'TouchPoints',
    canGoBack: true,
    requiresValidation: true,
    hasHeader: true,
    hasStartHeader: false,
    hasProgressDots: false,
    hasContinueButton: true,
  },
  {
    id: 'resume',
    component: 'Resume',
    canGoBack: true,
    requiresValidation: false,
    hasHeader: true,
    hasStartHeader: false,
    hasProgressDots: false,
    hasContinueButton: true,
  },
];

// Helper functions
export function getStepConfig(
  step: AmbassadorSignupStep
): StepConfig | undefined {
  return STEP_FLOW.find((config) => config.id === step);
}

export function getNextStep(
  currentStep: AmbassadorSignupStep
): AmbassadorSignupStep | null {
  const currentIndex = STEP_FLOW.findIndex(
    (config) => config.id === currentStep
  );

  if (currentIndex === -1 || currentIndex === STEP_FLOW.length - 1) {
    return null;
  }

  const nextStep = STEP_FLOW[currentIndex + 1].id;
  return nextStep;
}

export function getPreviousStep(
  currentStep: AmbassadorSignupStep
): AmbassadorSignupStep | null {
  const currentIndex = STEP_FLOW.findIndex(
    (config) => config.id === currentStep
  );
  if (currentIndex <= 0) {
    return null;
  }
  return STEP_FLOW[currentIndex - 1].id;
}

export function getStepIndex(step: AmbassadorSignupStep): number {
  return STEP_FLOW.findIndex((config) => config.id === step);
}

export function getTotalSteps(): number {
  return STEP_FLOW.length;
}

// Touchpoint helper functions
export function getFullTouchpointUrl(
  touchpointType: string,
  username: string
): string {
  const config = TOUCHPOINT_CONFIG[touchpointType];
  if (!config) {
    throw new Error(`Unknown touchpoint type: ${touchpointType}`);
  }

  // For touchpoints without prefix (like phone, email, calendar), return the value as-is
  if (config.prefix === null) {
    return username;
  }

  return config.prefix + username;
}

export function getDisplayPrefix(touchpointType: string): string | null {
  const config = TOUCHPOINT_CONFIG[touchpointType];
  if (!config) {
    throw new Error(`Unknown touchpoint type: ${touchpointType}`);
  }

  return config.displayPrefix;
}

export function autoPopulateTouchpoints(
  email: string,
  phoneNumber: string,
  existingUserTouchPoints: Array<{ touchPointId: number; value: string }> = []
): Array<{ touchPointId: number; value: string }> {
  // If user already has touchpoints, don't auto-populate
  if (existingUserTouchPoints.length > 0) {
    return existingUserTouchPoints;
  }

  const autoTouchpoints: Array<{ touchPointId: number; value: string }> = [];

  // Add email touchpoint if email exists
  if (email && email.trim() !== '') {
    autoTouchpoints.push({
      touchPointId: 11, // mail touchPointId
      value: email.trim(),
    });
  }

  // Add phone touchpoint if phone exists
  if (phoneNumber && phoneNumber.trim() !== '') {
    autoTouchpoints.push({
      touchPointId: 12, // phone touchPointId
      value: phoneNumber.trim(),
    });
  }

  return autoTouchpoints;
}
